import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { InputText } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BoxPlot } from "../../../components/graphs/BoxPlot"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { FEATURE_COMPARISON_DELIMITER, FEATURE_NESTING_SEPARATOR } from "../../../utils/constants"
import { ReportContext } from "../Context"
import type { FeaturesComparisonInsight, InsightProps } from "../interfaces"

/**
 * FeatureComparison Component
 *
 * Visualizes the comparison of features based on selected labels and reasons.
 * It fetches data from the API and displays it using a box plot.
 *
 * @param {Readonly<InsightProps>} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing the BoxPlot visualization
 */
export function FeatureComparison(props: Readonly<InsightProps>): React.ReactElement {
  const { insight } = props // Extract insight from props
  const { id: projectId } = useParams<{ id: string }>() // Get project ID from URL parameters

  const { report } = useContext(ReportContext) // Access report context

  const { t } = useTranslation("insights")

  // State variables to manage selected labels, reasons, and features for comparison
  const [selectedLabels, setSelectedLabels] = useState<string[] | null>(null)
  const [selectedReasons, setSelectedReasons] = useState<string[] | null>(null)
  const [selectedXFeature, setSelectedXFeature] = useState<string | undefined>(
    report?.features?.categorical?.[0] ?? undefined, // Default to the first categorical feature
  )
  const [selectedYFeature, setSelectedYFeature] = useState<string | undefined>(
    report?.features?.numerical?.[0] ?? undefined, // Default to the first numerical feature
  )

  // Fetch features counts insight data from the API
  const { data: featuresComparisonInsight, isLoading: isLoadingFeaturesComparisonInsight } = useQuery<
    AxiosResponse<FeaturesComparisonInsight>,
    AxiosError
  >(
    ["feature-comparison", projectId, report?.uuid, insight.uuid], // Query key
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      // Enable the query only when required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = useMemo(
    () =>
      report?.features?.categorical?.length === 0 ||
      report?.features?.numerical?.length === 0 ||
      Object.keys(featuresComparisonInsight?.data?.data ?? {}).length === 0,
    [featuresComparisonInsight?.data?.data, report?.features?.categorical, report?.features?.numerical],
  )

  // Memoized function to get available reasons based on selected labels
  const availableReasons = useMemo(() => {
    const reasons: string[] = []

    if (selectedLabels?.length === 0 || selectedLabels === null) {
      // If no labels are selected, reset reasons and return empty array
      setSelectedReasons(null)

      return []
    }

    for (const label of selectedLabels) {
      reasons.push(...Object.keys(featuresComparisonInsight?.data.data[label]?.reasons ?? {}))
    }

    // Filter selected reasons to only keep those that are still available
    const filteredReasons = selectedReasons?.filter((reason) => reasons.includes(reason))

    // Update selected reasons - set to null if no reasons are left, otherwise update with filtered list
    if (!filteredReasons || filteredReasons.length === 0) {
      setSelectedReasons(null)
    } else {
      setSelectedReasons(filteredReasons)
    }

    return reasons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresComparisonInsight, selectedLabels])

  // Memoized function to transform and prepare box plot data based on selections
  const adjustedBoxPlotData = useMemo(() => {
    const result: Record<
      string,
      | Record<
          string,
          | {
              max: number | null
              min: number | null
              mean: number | null
              count: number
              "25th_percentile": number | null
              "50th_percentile": number | null
              "75th_percentile": number | null
            }
          | undefined
        >
      | undefined
    > = {}

    if (isLoadingFeaturesComparisonInsight || insight.status !== InsightList.status.SUCCEEDED) return {}

    const labelsToProcess = selectedLabels ?? ["__all_labels__"]

    if (selectedReasons === null || selectedReasons.length === 0) {
      labelsToProcess.forEach((label) => {
        if (
          !!featuresComparisonInsight?.data.data[label]?.features[
            `${selectedXFeature}${FEATURE_COMPARISON_DELIMITER}${selectedYFeature}`
          ]
        )
          result[`${label === "__all_labels__" ? "All labels" : label}`] =
            featuresComparisonInsight?.data.data[label].features[
              `${selectedXFeature}${FEATURE_COMPARISON_DELIMITER}${selectedYFeature}`
            ]
      })
    } else {
      labelsToProcess.forEach((label) => {
        selectedReasons.forEach((reason) => {
          const reasons = featuresComparisonInsight?.data.data[label]?.reasons

          if (
            reasons?.hasOwnProperty(reason) &&
            reasons[reason]?.hasOwnProperty(`${selectedXFeature}${FEATURE_COMPARISON_DELIMITER}${selectedYFeature}`) &&
            reasons?.[reason][`${selectedXFeature}${FEATURE_COMPARISON_DELIMITER}${selectedYFeature}`]
          ) {
            result[
              `${label === "__all_labels__" ? `${t("FeatureComparison.filters.labels.all")} -> ${reason}` : `${label} -> ${reason}`}`
            ] = reasons?.[reason][`${selectedXFeature}${FEATURE_COMPARISON_DELIMITER}${selectedYFeature}`]
          }
        })
      })
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featuresComparisonInsight?.data.data,
    isLoadingFeaturesComparisonInsight,
    selectedLabels,
    selectedReasons,
    selectedXFeature,
    selectedYFeature,
  ])

  // Handler for label selection changes
  const handleLabelSelect = (selectedOptions: string[]): void => {
    // If no options are selected, reset to all labels
    if (selectedOptions.length === 0) {
      setSelectedLabels(null)
      return
    }

    setSelectedLabels(selectedOptions)
  }

  // Handler for reason selection changes
  const handleReasonSelect = (selectedOptions: string[]): void => {
    // If no options are selected, reset to all labels
    if (selectedOptions.length === 0) {
      setSelectedReasons(null)
      return
    }

    setSelectedReasons(selectedOptions)
  }

  // Renders the subheader component with selection controls
  const subHeader = (): React.ReactElement => {
    return (
      <Grid container columnSpacing={1}>
        <Grid item xs={3}>
          <InputText
            label={t("FeatureComparison.filters.decisions.label")}
            options={Object.keys(featuresComparisonInsight?.data?.data ?? {})?.filter(
              (label) => label !== "__all_labels__",
            )}
            multipleModeProps={{
              isActive: true,
              hideChips: true,
              selectedValues: selectedLabels ?? [],
              setSelectedValues: (selectedOptions) => handleLabelSelect(selectedOptions as string[]),
              allowDeselect: (selectedLabels?.length ?? 0) > 0,
            }}
            value={
              selectedLabels === null
                ? t("FeatureComparison.filters.decisions.all")
                : selectedLabels.length && selectedLabels?.length > 0
                  ? t("FeatureComparison.filters.decisions.selected", { count: selectedLabels?.length })
                  : t("FeatureComparison.filters.decisions.select")
            }
            isLoadingOptions={isLoadingFeaturesComparisonInsight}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item xs={3}>
          <InputText
            label={t("FeatureComparison.filters.reasons.label")}
            options={availableReasons ?? []}
            value={
              selectedReasons === null
                ? t("FeatureComparison.filters.reasons.all")
                : selectedReasons?.length && selectedReasons?.length > 0
                  ? t("FeatureComparison.filters.reasons.selected", { count: selectedReasons?.length })
                  : t("FeatureComparison.filters.reasons.select")
            }
            multipleModeProps={{
              isActive: true,
              hideChips: true,
              selectedValues: selectedReasons ?? [],
              setSelectedValues: (selectedOptions) => handleReasonSelect(selectedOptions as string[]),
              allowDeselect: (selectedReasons?.length ?? 0) > 0,
            }}
            isLoadingOptions={isLoadingFeaturesComparisonInsight}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item xs={3}>
          <InputText
            label={t("FeatureComparison.filters.xFeature")}
            optionsWithValues={(report?.features?.categorical ?? []).map((feature) => {
              return { label: feature.replaceAll(FEATURE_NESTING_SEPARATOR, "."), value: feature }
            })}
            value={selectedXFeature}
            handleChange={(e) => setSelectedXFeature(e.target.value as string)}
            isLoadingOptions={isLoadingFeaturesComparisonInsight}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item xs={3}>
          <InputText
            label={t("FeatureComparison.filters.yFeature")}
            optionsWithValues={(report?.features?.numerical ?? []).map((feature) => {
              return { label: feature.replaceAll(FEATURE_NESTING_SEPARATOR, "."), value: feature }
            })}
            value={selectedYFeature}
            handleChange={(e) => setSelectedYFeature(e.target.value as string)}
            isLoadingOptions={isLoadingFeaturesComparisonInsight}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item xs={12}>
      <BoxPlot
        scrollable
        title={t("FeatureComparison.title")}
        tooltipText={<Trans>{t("FeatureComparison.tooltip")}</Trans>}
        data={adjustedBoxPlotData ?? {}}
        subHeader={!isEmpty ? subHeader() : undefined}
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("FeatureComparison.unavailable") }
              : undefined
        }
        isLoading={isLoadingFeaturesComparisonInsight}
        graphHeight={440}
      />
    </Grid>
  )
}
